import { supabase } from '../supabase/client';

/**
 * Get dashboard metrics
 */
export const getDashboardMetrics = async () => {
  try {
    // Get counts in parallel
    const [customersResult, casesResult, transactionsResult] = await Promise.all([
      // Active customers count
      supabase
        .from('users')
        .select('id', { count: 'exact', head: true })
        .eq('account_status', 'active'),

      // Open cases count
      supabase
        .from('support_cases')
        .select('id', { count: 'exact', head: true })
        .in('status', ['open', 'pending']),

      // Pending transactions (example - you may need to adjust)
      supabase
        .from('transactions')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending'),
    ]);

    return {
      success: true,
      data: {
        active_customers: customersResult.count || 0,
        open_cases: casesResult.count || 0,
        pending_transactions: transactionsResult.count || 0,
        system_health: 'ok', // Simplified for now
      },
    };
  } catch (error: any) {
    console.error('[MetricsService] Failed to fetch dashboard metrics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get quick metrics (today and this week)
 */
export const getQuickMetrics = async () => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayISO = today.toISOString();

    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    weekAgo.setHours(0, 0, 0, 0);
    const weekAgoISO = weekAgo.toISOString();

    // Get metrics in parallel
    const [todayLoginsResult, todayTransactionsResult, todayCustomersResult,
           weekLoginsResult, weekTransactionsResult, weekCustomersResult] = await Promise.all([
      // Today's logins
      supabase
        .from('sessions')
        .select('id', { count: 'exact', head: true })
        .gte('start_time', todayISO),

      // Today's transactions
      supabase
        .from('transactions')
        .select('id', { count: 'exact', head: true })
        .gte('transaction_timestamp', todayISO),

      // Today's new customers
      supabase
        .from('users')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', todayISO),

      // This week's logins
      supabase
        .from('sessions')
        .select('id', { count: 'exact', head: true })
        .gte('start_time', weekAgoISO),

      // This week's transactions
      supabase
        .from('transactions')
        .select('id', { count: 'exact', head: true })
        .gte('transaction_timestamp', weekAgoISO),

      // This week's new customers
      supabase
        .from('users')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', weekAgoISO),
    ]);

    return {
      success: true,
      data: {
        today: {
          logins: todayLoginsResult.count || 0,
          transactions: todayTransactionsResult.count || 0,
          new_customers: todayCustomersResult.count || 0,
        },
        this_week: {
          logins: weekLoginsResult.count || 0,
          transactions: weekTransactionsResult.count || 0,
          new_customers: weekCustomersResult.count || 0,
        },
      },
    };
  } catch (error: any) {
    console.error('[MetricsService] Failed to fetch quick metrics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get recent activity feed
 */
export const getRecentActivity = async (limit: number = 10) => {
  try {
    const { data, error } = await supabase
      .from('staff_activity_log')
      .select(`
        *,
        staff_user:staff_users(first_name, last_name)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('[MetricsService] Failed to fetch activity feed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get assigned cases for current staff user
 */
export const getMyPriorityQueue = async (staffUserId: string, limit: number = 5) => {
  try {
    const { data, error } = await supabase
      .from('support_cases')
      .select(`
        *,
        customer:users(id, first_name, last_name, phone_number)
      `)
      .eq('assigned_to', staffUserId)
      .in('status', ['open', 'pending'])
      .order('sla_due_at', { ascending: true })
      .limit(limit);

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error('[MetricsService] Failed to fetch priority queue:', error);
    return { success: false, error: error.message };
  }
};

export const metricsService = {
  getDashboardMetrics,
  getQuickMetrics,
  getRecentActivity,
  getMyPriorityQueue,
};

